<script setup lang="ts">
import type { Item as KMultipleAutocompleteFieldItem } from '@/components/fields/KMultipleAutocompleteField.vue';

import { computed, nextTick, reactive, ref, toRaw } from 'vue';
import { watchDebounced } from '@vueuse/core';
import _isEqual from 'lodash/isEqual';

import { stateShortNames } from '@/utils/PortalConstants';
import DashboardFiltersBtn from '@/components/btns/DashboardFiltersBtn.vue';
import TableActionBtn from '@/components/btns/TableActionBtn.vue';
import KMultipleAutocompleteField from '@/components/fields/KMultipleAutocompleteField.vue';
import KMultipleSelectField from '@/components/fields/KMultipleSelectField.vue';
import KTextField from '@/components/fields/KTextField.vue';
import StateMultiSelectField from '@/components/fields/StateMultiSelectField.vue';
import DatePickerField from '@/components/pickerFields/DatePickerField.vue';
import useForm from '@/composables/useForm';
import { requiredValidationRule } from '@/helpers/validationRules';

export interface InitialData {
  date?: string | null;
  states?: string[] | null;
  search?: string | null;
  drivers?: number[] | null;
  statuses?: string[] | null;
  attributes?: string[] | null;
}

export interface ValidData {
  date: string;
  states: string[] & { 0: string };
  search?: string | null;
  drivers?: number[] | null;
  statuses?: string[] | null;
  attributes?: string[] | null;
}

export interface Props {
  loading?: boolean;
  initialData?: InitialData;
  driversItems?: readonly number[];
  statusesItems?: readonly KMultipleAutocompleteFieldItem[];
  attributesItems?: readonly KMultipleAutocompleteFieldItem[];
  isStaff?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  loading: false,
  initialData: () => ({}),
  driversItems: () => [],
  statusesItems: () => [],
  attributesItems: () => [],
  isStaff: false,
});
const emit = defineEmits<{
  submit: [formData: ValidData];
}>();

const formData = reactive<InitialData>({});
const isValid = ref(true);

const { form, handleSubmit } = useForm(props, formData, () => {
  emit('submit', toRaw(formData) as ValidData);
});

const filtersCount = computed(() => {
  let statesLength = formData.states?.length ?? 0;
  if (_isEqual(formData.states, stateShortNames)) statesLength = 0;
  return (
    statesLength +
    (formData.drivers?.length ?? 0) +
    (formData.statuses?.length ?? 0) +
    (formData.attributes?.length ?? 0)
  );
});

const showFiltersBar = ref(!!filtersCount.value);

function handleFiltersCountClick(): void {
  showFiltersBar.value = !showFiltersBar.value;
}

function handleClearAllFiltersClick(): void {
  formData.states = stateShortNames; // same variables in useRouteQuery()
  formData.drivers = [];
  formData.statuses = [];
  formData.attributes = [];
}

watchDebounced(
  [
    () => formData.date,
    () => formData.states,
    () => formData.search,
    () => formData.drivers,
    () => formData.statuses,
    () => formData.attributes,
  ],
  async (newFormDataArr, oldFormDataArr) => {
    if (!_isEqual(newFormDataArr, oldFormDataArr)) {
      await handleSubmit();
    }
  },
  { deep: true, debounce: 500 },
);

function handleStatesClear(event: Event): void {
  void nextTick(() => {
    formData.states = stateShortNames; // same variables in handleClearAllFiltersClick()
  });
}
</script>

<template>
  <v-form
    ref="form"
    v-model="isValid"
    validate-on="lazy"
    :class="$style.dashboardSearchForm"
    data-testid="dashboardSearchForm"
    @submit.prevent="handleSubmit"
  >
    <div :class="$style.mainFieldGroup">
      <div :class="$style.mainFields">
        <DatePickerField
          v-model="formData.date"
          size="lg"
          label="Date"
          :rules="[requiredValidationRule]"
          :class="$style.field"
        />
        <KTextField
          v-model="formData.search"
          size="lg"
          label="Search"
          :class="$style.field"
          placeholder="Type to search..."
          clearable
        />
      </div>
      <div :class="$style.filtersBtns">
        <TableActionBtn
          :class="$style.clearAllFiltersBtn"
          icon="mdi-filter-remove-outline"
          :disabled="!filtersCount"
          @click="handleClearAllFiltersClick"
        >
          Clear all filters
        </TableActionBtn>
        <DashboardFiltersBtn
          :count="filtersCount"
          :is-active="!!filtersCount"
          @click="handleFiltersCountClick"
        />
      </div>
    </div>

    <v-expand-transition>
      <div v-if="showFiltersBar" :class="$style.filters">
        <div :class="$style.filtersLabel">Filter by</div>
        <div :class="$style.filtersFields">
          <StateMultiSelectField
            v-model="formData.states"
            size="large"
            label="State"
            :rules="[requiredValidationRule]"
            :class="[$style.field, $style.multiSelectField]"
            @click:clear="handleStatesClear"
            @keydown.enter="handleSubmit"
          />
          <KMultipleAutocompleteField
            v-if="props.isStaff"
            v-model="formData.drivers"
            size="lg"
            label="Drivers"
            :class="[$style.field, $style.multiSelectField]"
            :items="props.driversItems"
            @keydown.enter="handleSubmit"
          />
          <KMultipleSelectField
            v-model="formData.statuses"
            size="lg"
            label="Statuses"
            :class="[$style.field, $style.multiSelectField]"
            :items="props.statusesItems"
            @keydown.enter="handleSubmit"
          />
          <KMultipleAutocompleteField
            v-model="formData.attributes"
            size="lg"
            label="Attributes"
            :class="[$style.field, $style.multiSelectField]"
            :items="props.attributesItems"
            @keydown.enter="handleSubmit"
          />
        </div>
      </div>
    </v-expand-transition>
    <input type="submit" class="hidden" :disabled="props.loading" />
  </v-form>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.dashboardSearchForm {
}
  .field {
    width: 17.5rem;
    display: inline-block;
    vertical-align: top;

    &.multiSelectField {
      width: 26rem;
    }
  }
  .field {
    margin-right: 1.5rem;
    margin-bottom: 0.5rem;
  }

  .mainFieldGroup {
    display: flex;
  }
    .mainFields {
      width: 100%;
    }
    .filtersBtns {
      flex-shrink: 0;
    }
      .clearAllFiltersBtn {
        margin-right: 15px;
        font-size: 18px;
      }

  .filters {
    background-color: colors.$color-gray-50;
    border-radius: 8px;
    display: flex;
    align-items: flex-start;
    margin-top: 2px;
    margin-bottom: 30px;

    .field {
      margin-bottom: 0;
    }
  }
    .filtersLabel {
      display: flex;
      align-items: center;
      min-height: 92px;
      padding: 0 1.5rem;
      white-space: nowrap;
    }
    .filtersFields {
      margin-top: 22px;
    }
</style>
